/**
 * Cursor-spoof belief layer (06-timeline.md §13.3, #254).
 *
 * "Spoofed" is one of the five cursor-visibility modes (#255). When a
 * RevealTarget is set to `spoofed`, the GM writes a SYNTHETIC BELIEF FACT
 * owned by the affected Character(s):
 *
 * ```
 * owner:     belief(Character C)
 * subject:   C
 * predicate: CURSOR_SPOOF_PREDICATE   ("__cursor_spoof")
 * value:     { believed_day: <canonical day the character believes is "now"> }
 * status:    canon   (visible to the believeing Character's projection)
 * tag:       cursor_spoof  (present in `meta_tags` for auditor detection)
 * valid_from: <session cursor day at spoof start>   (the spoof window)
 * valid_to:   <open>  — set when spoof is retired; never deleted (append-only)
 * ```
 *
 * For `party` or `user` RevealTargets, the GM writes one belief per affected
 * member Character. Ending the spoof closes `valid_to` — it does NOT delete
 * the fact (op log is append-only).
 *
 * The LLM NEVER authors spoof beliefs — this is GM-only authoring.
 *
 * **Design note on predicate identity.** The open-vocabulary model (ADR-0004)
 * uses opaque minted `pred_…` ids for user-extensible predicates. This spoof
 * belief uses a RESERVED `__cursor_spoof` key (like `__entity`, `__campaign`,
 * `__cursor_visibility`) — it is a system-internal fact, not user-declarable.
 * The `__` prefix is enforced as a reservation by the pack validator.
 *
 * **Design note on `meta_tags`.** `FactMetaShape` does not yet have a
 * `meta_tags` field (it is a future Phase 2 surface). For now the spoof belief
 * is identified by its predicate key (`CURSOR_SPOOF_PREDICATE`) and its
 * `owner.kind === 'belief'`, both of which are already present in `FactState`.
 * The auditor detects active spoof beliefs by searching the entity graph for
 * facts on `CURSOR_SPOOF_PREDICATE` with `owner.kind === 'belief'` and
 * `validity.to` absent (still active). The `cursor_spoof` tag in the design
 * doc maps to this detection path.
 */

import { z } from 'zod';

// ─── Reserved predicate key ───────────────────────────────────────────────────

/**
 * The reserved system predicate key for cursor-spoof belief facts.
 *
 * Uses the `__` prefix (reserved system namespace, like `__entity` and
 * `__campaign`) so it can never collide with a user-declared `pred_…` id.
 * Written as the `predicate` slot in the op log for any spoof belief fact.
 */
export const CURSOR_SPOOF_PREDICATE = '__cursor_spoof';

// ─── Value shape ──────────────────────────────────────────────────────────────

/**
 * The value stored in a cursor-spoof belief fact.
 *
 * `believed_day` is a canonical day count — the in-world date the Character
 * BELIEVES is "now". It is stored as an exact canonical day (integers) so it
 * participates in the same calendar-arithmetic as every other day value in the
 * system; display conversion happens at the boundary.
 */
export const SpoofBeliefValueSchema = z
  .object({
    believed_day: z.number().int(),
  })
  .strict();
export type SpoofBeliefValue = z.infer<typeof SpoofBeliefValueSchema>;

/** Build a spoof-belief value payload. */
export function spoofBeliefValue(believed_day: number): SpoofBeliefValue {
  return { believed_day };
}

// ─── Active-spoof record (read model) ────────────────────────────────────────

/**
 * A resolved active-spoof record: the `believed_day` the Character currently
 * holds as "now", derived from a `CURSOR_SPOOF_PREDICATE` belief fact that is:
 *
 * - `owner.kind === 'belief'`
 * - `status === 'canon'`
 * - `soft_deleted === false`
 * - `validity.to === undefined` (still open — not yet retired)
 * - `validity.from <= sessionDay` (started before or at the current session day)
 * - value parses as `SpoofBeliefValue`
 *
 * Returned by `resolveActiveSpoofBelief`; consumed by the projection chokepoint
 * and the auditor's spoof-in-effect rule.
 */
export interface ActiveSpoofBelief {
  /** The canonical day the Character believes is "now" (the spoofed cursor). */
  believed_day: number;
  /**
   * The entity id of the Character holding this belief (the `subject` of the
   * op that created the belief fact).
   */
  character_id: string;
  /**
   * The canonical day the spoof started (`validity.from` on the belief fact).
   * Used by the auditor to report "N facts authored since spoof began".
   */
  started_at_day: number;
}

// ─── Detection helpers ────────────────────────────────────────────────────────

import type { EntityState, FactState } from './entities-fold.js';
import type { ViewerId } from './viewer.js';

/**
 * Resolve the ACTIVE spoof belief for a Viewer (Character), if one exists.
 *
 * Scans the entity's fact map for a `CURSOR_SPOOF_PREDICATE` fact that is
 * currently active (canon, not soft-deleted, valid_to open, valid_from ≤
 * `sessionDay`). Returns the first match, or `null` if none.
 *
 * This is a READ helper for the projection chokepoint and the auditor. It
 * does NOT construct `VisibleFact` — that remains the sole responsibility of
 * `packages/projection`.
 *
 * @param entity     The folded `EntityState` of the Character entity.
 * @param viewer     The Viewer whose perspective is being projected (must be
 *                   `kind === 'character'`; the Character entity id must match).
 * @param sessionDay The current session cursor day (canonical). Used to check
 *                   `validity.from <= sessionDay`.
 */
export function resolveActiveSpoofBelief(
  entity: EntityState,
  viewer: ViewerId,
  sessionDay: number,
): ActiveSpoofBelief | null {
  if (viewer.kind !== 'character') return null;

  // Scan facts for the spoof predicate (may include instance-keyed versions;
  // we check any key that starts with CURSOR_SPOOF_PREDICATE).
  for (const [factKey, fact] of Object.entries(entity.facts)) {
    // Only look at spoof-predicate facts (bare key or instance-keyed).
    const predicate = factKey.includes('#') ? factKey.split('#')[0] : factKey;
    if (predicate !== CURSOR_SPOOF_PREDICATE) continue;

    if (!isActiveSpoofFact(fact, sessionDay)) continue;

    const value = currentSpoofValue(fact);
    if (value === null) continue;

    return {
      believed_day: value.believed_day,
      character_id: entity.id,
      started_at_day: fact.validity?.from ?? sessionDay,
    };
  }

  return null;
}

/**
 * Collect ALL active spoof beliefs across every entity in the campaign.
 *
 * Used by the auditor's spoof-in-effect rule to find which Characters are
 * currently under a spoof, and how long (for the "N facts authored" advisory).
 *
 * Returns one `ActiveSpoofBelief` per Character with an active spoof,
 * sorted by `character_id` for deterministic ordering.
 */
export function collectActiveSpoofBeliefs(
  entities: Record<string, EntityState>,
  sessionDay: number,
): ActiveSpoofBelief[] {
  const out: ActiveSpoofBelief[] = [];

  for (const [entityId, entity] of Object.entries(entities)) {
    for (const [factKey, fact] of Object.entries(entity.facts)) {
      const predicate = factKey.includes('#') ? factKey.split('#')[0] : factKey;
      if (predicate !== CURSOR_SPOOF_PREDICATE) continue;
      if (!isActiveSpoofFact(fact, sessionDay)) continue;
      const value = currentSpoofValue(fact);
      if (value === null) continue;
      out.push({
        believed_day: value.believed_day,
        character_id: entityId,
        started_at_day: fact.validity?.from ?? sessionDay,
      });
      // one active spoof per character is the invariant; skip remaining facts
      break;
    }
  }

  return out.sort((a, b) => (a.character_id < b.character_id ? -1 : 1));
}

// ─── Internal helpers ─────────────────────────────────────────────────────────

/**
 * True if this `FactState` is a live, active cursor-spoof belief as of
 * `sessionDay`. All five conditions must hold:
 * 1. `owner.kind === 'belief'`
 * 2. `status === 'canon'`
 * 3. `soft_deleted === false`
 * 4. `validity.to` is absent (still open — spoof not yet retired)
 * 5. `validity.from` ≤ `sessionDay` (spoof has started)
 */
function isActiveSpoofFact(fact: FactState, sessionDay: number): boolean {
  if (fact.owner.kind !== 'belief') return false;
  if (fact.status !== 'canon') return false;
  if (fact.soft_deleted) return false;
  if (fact.validity?.to !== undefined) return false; // retired
  if (fact.validity?.from !== undefined && fact.validity.from > sessionDay) return false;
  return true;
}

/**
 * Parse the current value of a spoof-belief fact. Returns `null` if the
 * value is missing or malformed (projection degrades gracefully).
 */
function currentSpoofValue(fact: FactState): SpoofBeliefValue | null {
  const raw = fact.versions.length > 0 ? fact.versions[fact.versions.length - 1]!.value : undefined;
  if (raw === undefined) return null;
  const parsed = SpoofBeliefValueSchema.safeParse(raw);
  return parsed.success ? parsed.data : null;
}
